/**
 * API Configuration System
 * Implements Open/Closed Principle by making API endpoints configurable
 */
import { HOST_URL, API_V1, API_V2, API_V3 } from '@/config/api';

/**
 * API endpoint configuration
 */
export class ApiConfig {
  constructor(baseUrl = HOST_URL) {
    this.baseUrl = baseUrl;
    this.endpoints = {
      // Exhibit endpoints
      exhibits: {
        list: (groupId, memberLineId) =>
          `${baseUrl}${API_V2}/groups/${groupId}/exhibits?line_id=${memberLineId}`,
        unlock: (storyId) =>
          `${baseUrl}${API_V3}/stories/${storyId}/unlock_exhibits`
      },

      // Tool endpoints
      tools: {
        getNumber: (memberLineId) => 
          `${baseUrl}${API_V1}/line/get_tool_num?line_id=${memberLineId}`
      },
      
      // Records endpoints
      records: {
        list: (groupId, memberLineId) => 
          `${baseUrl}${API_V2}/groups/${groupId}/records?line_id=${memberLineId}`
      },
      
      // Hints endpoints
      hints: {
        list: (groupId, memberLineId) => 
          `${baseUrl}${API_V2}/groups/${groupId}/hints?line_id=${memberLineId}`
      },
      
      // Souls endpoints
      souls: {
        list: (groupId, memberLineId) => 
          `${baseUrl}${API_V2}/groups/${groupId}/souls?line_id=${memberLineId}`
      },
      
      // Times endpoints
      times: {
        list: (groupId, memberLineId) => 
          `${baseUrl}${API_V2}/groups/${groupId}/times?line_id=${memberLineId}`
      },
      
      // Related endpoints
      relateds: {
        list: (groupId, memberLineId) => 
          `${baseUrl}${API_V2}/groups/${groupId}/relateds?line_id=${memberLineId}`
      },
      
      // Yama (judgment) endpoints
      yama: {
        list: (groupId, memberLineId) => 
          `${baseUrl}${API_V2}/groups/${groupId}/judgment?line_id=${memberLineId}`
      }
    };
  }

  /**
   * Get endpoint URL
   * @param {string} category - Endpoint category
   * @param {string} action - Action name
   * @param {...any} params - URL parameters
   * @returns {string} Complete URL
   */
  getEndpoint(category, action, ...params) {
    const categoryEndpoints = this.endpoints[category];
    if (!categoryEndpoints) {
      throw new Error(`Unknown API category: ${category}`);
    }

    const endpointBuilder = categoryEndpoints[action];
    if (!endpointBuilder) {
      throw new Error(`Unknown API action: ${action} in category: ${category}`);
    }

    if (typeof endpointBuilder === 'function') {
      return endpointBuilder(...params);
    }
    
    return endpointBuilder;
  }

  /**
   * Add custom endpoint
   * @param {string} category - Endpoint category
   * @param {string} action - Action name
   * @param {string|Function} endpoint - Endpoint URL or builder function
   */
  addEndpoint(category, action, endpoint) {
    if (!this.endpoints[category]) {
      this.endpoints[category] = {};
    }
    this.endpoints[category][action] = endpoint;
  }

  /**
   * Override existing endpoint
   * @param {string} category - Endpoint category
   * @param {string} action - Action name
   * @param {string|Function} endpoint - New endpoint URL or builder function
   */
  overrideEndpoint(category, action, endpoint) {
    if (!this.endpoints[category] || !this.endpoints[category][action]) {
      throw new Error(`Endpoint ${category}.${action} does not exist`);
    }
    this.endpoints[category][action] = endpoint;
  }

  /**
   * Get all endpoints for a category
   * @param {string} category - Endpoint category
   * @returns {Object} Category endpoints
   */
  getCategoryEndpoints(category) {
    return this.endpoints[category] || {};
  }
}

// Default API configuration instance
export const apiConfig = new ApiConfig();

/**
 * Convenience functions for common endpoints
 */
export const getExhibitsUrl = (groupId, memberLineId) => 
  apiConfig.getEndpoint('exhibits', 'list', groupId, memberLineId);

export const getUnlockExhibitsUrl = (storyId) =>
  apiConfig.getEndpoint('exhibits', 'unlock', storyId);

export const getToolNumberUrl = (memberLineId) =>
  apiConfig.getEndpoint('tools', 'getNumber', memberLineId);